/**
 * Catalog row variant picker.
 *
 * A self-contained custom element used in the cart-page predictive search
 * results and the collection rows view. It deliberately avoids Shopify's
 * native <variant-picker> because:
 *
 *   1. <variant-picker> fetches the product page on every change and morphs
 *      its own subtree from the response. That's overkill in a list view
 *      and breaks the row's styling/structure.
 *   2. Its VariantSelectedEvent flips product-form-component into
 *      `#variantChangeInProgress = true`, after which clicking "Add to
 *      order" pushes a {id, quantity} entry onto an internal queue. The
 *      queue is flushed via JSON POST that drops the form's line item
 *      properties (Patient Name, Cylinder, sAdd) entirely.
 *
 * This picker keeps product-form submission simple: on change, find the
 * matching variant, write its id into the hidden input and update the
 * price. For products with more variants than Liquid exposes in
 * product.variants, it asks Shopify's product page resolver for the
 * selected option-value IDs. No events bubble up to product-form-component,
 * so the normal form submission path runs and FormData carries everything.
 *
 * Every variant continue-sells when out of stock, so stock is never a
 * reason to disable anything. Option <select>s are left fully selectable;
 * if the user picks a combination that doesn't exist as a variant, the
 * Add-to-cart button is disabled instead of greying out individual
 * dropdown values (which produced large confusing blocks of disabled
 * options on sparse-variant products).
 *
 * Correctness invariant — the id submitted to the cart must always match
 * the visible selection. Three things defend it:
 *   1. Remote resolutions are only accepted when the returned variant's
 *      options equal the current selection (the resolver section falls
 *      back to the product's first variant for nonexistent combinations).
 *   2. Listeners are re-attached on every reconnect, because morph-driven
 *      row reorders detach the element and would otherwise leave a picker
 *      that no longer reacts to option changes.
 *   3. A capture-phase click guard on the Add button re-derives the
 *      variant id from the current select values immediately before the
 *      form's FormData is read, and blocks submission entirely when the
 *      selection can't be verified.
 */
(() => {
    if (customElements.get('catalog-row-picker')) return;

    /** @typedef {{id: number, option1: string | null, option2: string | null, option3: string | null, price: number | null, compare_at_price: number | null}} Variant */

    /**
     * Shopify's `variant | json` exposes options as `option1`, `option2`,
     * `option3` — there is no `options` array on the JSON shape. Pull them
     * into a normalized array of strings for easy matching.
     * @param {Variant} v
     * @returns {string[]}
     */
    const variantOptions = (v) =>
        [v.option1, v.option2, v.option3]
            .filter((x) => x !== null && x !== undefined)
            .map((x) => String(x));

    class CatalogRowPicker extends HTMLElement {
        /** @type {Variant[]} */
        #variants = [];
        /** @type {AbortController | null} */
        #abortController = null;
        /** @type {string | null} */
        #requestKey = null;
        /**
         * Last variant confirmed by the remote resolver, keyed by the
         * option-value-id signature it was resolved for.
         * @type {{key: string, id: string} | null}
         */
        #lastResolved = null;
        /** @type {Element | null} */
        #row = null;

        connectedCallback() {
            // When the HTML parser encounters our opening tag it calls
            // connectedCallback immediately — BEFORE the inner <select>s
            // and the variants <script> have been parsed. Try to init now;
            // if children aren't ready, observe and retry until they are.
            if (this.#initialized) {
                // Morph reorders move nodes via insertBefore, which fires
                // disconnectedCallback (removing our listeners) and then this
                // callback again. Re-attach, otherwise the picker goes dead:
                // option changes stop updating the hidden variant id and the
                // form submits whatever id it happened to hold — not what the
                // customer selected.
                this.#attachListeners();
                return;
            }

            if (this.#tryInit()) return;

            this.#observer = new MutationObserver(() => {
                if (this.#tryInit()) {
                    this.#observer?.disconnect();
                    this.#observer = null;
                }
            });
            this.#observer.observe(this, { childList: true, subtree: true });
        }

        #initialized = false;
        /** @type {MutationObserver | null} */
        #observer = null;

        /** @returns {boolean} true if init succeeded. */
        #tryInit() {
            if (this.#initialized) return true;
            const jsonEl = this.querySelector('script[type="application/json"][data-catalog-variants]');
            const hasSelects = this.querySelector('select[name^="options"]') !== null;
            // Wait for both the variants JSON and at least one option select
            // (or a default-variant product where there are no option selects
            // but the JSON is enough to read the single variant).
            if (!jsonEl) return false;

            this.#initialized = true;
            this.#refreshVariantsFromDom();

            this.#attachListeners();
            // If the picker has no option selects (default variant only),
            // there's nothing to sync.
            if (hasSelects) this.#syncSelectedVariant(false);
            return true;
        }

        /**
         * Attach the change listener and the add-button click guard. Safe to
         * call repeatedly — addEventListener dedupes identical listeners —
         * and called again on every reconnect because morph moves detach us.
         */
        #attachListeners() {
            this.addEventListener('change', this.#onChange);

            const row = this.closest('.catalog-row');
            if (row !== this.#row) {
                this.#row?.removeEventListener('click', this.#onAddClick, true);
                this.#row = row;
            }
            // Capture phase: must run during the click, before the browser
            // dispatches the form's submit event (which product-form-component
            // handles at document level and reads FormData synchronously).
            this.#row?.addEventListener('click', this.#onAddClick, true);
        }

        disconnectedCallback() {
            this.#observer?.disconnect();
            this.#observer = null;
            this.#abortController?.abort();
            this.#abortController = null;
            this.removeEventListener('change', this.#onChange);
            this.#row?.removeEventListener('click', this.#onAddClick, true);
        }

        #onChange = (event) => {
            // Only react to the variant option selects (name="options[...]").
            // Metafield / property selects share the same wrapper but use
            // name="properties[...]" and shouldn't trigger variant matching.
            const target = event.target;
            if (!(target instanceof HTMLSelectElement)) return;
            if (!target.name.startsWith('options[')) return;

            this.#syncSelectedVariant(true);
        };

        /**
         * Find the variant matching the current selection and push its id
         * into the form's hidden input. Also updates the price display.
         */
        async #syncSelectedVariant(disableWhileResolving = true) {
            // Re-read the variants JSON from the DOM on every sync. The picker
            // element is reused across morph updates (e.g. facet filtering and
            // pagination of the catalog rows), so the original `#variants`
            // captured in `connectedCallback` may belong to a DIFFERENT product
            // than the one currently rendered into this row. Without this
            // refresh, selecting an option would resolve a variant id from the
            // OLD product's list and write it into the form's hidden `name="id"`
            // input — causing add-to-cart to add the wrong (often the first
            // pre-filter) item.
            this.#refreshVariantsFromDom();

            const selects = this.#optionSelects();
            if (selects.length === 0) return;

            const selected = selects.map((s) => s.value);
            const selectedOptionValueIds = selects.map(
                (select) => select.selectedOptions[0]?.dataset.optionValueId ?? ''
            );
            const requestKey = selectedOptionValueIds.join(',');
            const variant = this.#variants.find((v) => this.#matchesSelection(v, selected)) || null;

            const row = this.closest('.catalog-row');
            if (!row) return;

            if (variant) {
                this.#requestKey = null;
                this.#setFormState(row, variant);
                this.#updatePrice(row, variant);
                this.#updateStock(row, variant);
                return;
            }

            if (disableWhileResolving) {
                this.#setPendingState(row);
            }

            await this.#resolveSelectedVariant(row, selected, selectedOptionValueIds, requestKey);
        }

        /**
         * @param {Variant} variant
         * @param {string[]} selected - The option select values, in order.
         * @returns {boolean} true if the variant's options equal the selection.
         */
        #matchesSelection(variant, selected) {
            const opts = variantOptions(variant);
            return selected.every((val, i) => opts[i] === val);
        }

        /**
         * Resolve the selected combination remotely, validate it, and apply it
         * to the row's form. The resolver section renders
         * `selected_or_first_available_variant`, which silently falls back to
         * the product's FIRST variant when the requested combination doesn't
         * exist. Trusting that fallback would write the default variant's id
         * into the form while the selects still show the customer's choice —
         * the wrong item would be added to the cart. Only a resolved variant
         * whose options match the selection is accepted; otherwise the Add
         * button is disabled (the intended behavior for combinations that
         * don't exist as variants).
         * @param {Element} row
         * @param {string[]} selected
         * @param {string[]} selectedOptionValueIds
         * @param {string} requestKey
         * @returns {Promise<boolean>} true if the form now holds a verified variant id.
         */
        async #resolveSelectedVariant(row, selected, selectedOptionValueIds, requestKey) {
            const resolvedVariant = await this.#fetchSelectedVariant(selectedOptionValueIds, requestKey);
            if (this.#requestKey !== requestKey || !this.isConnected) return false;

            if (!resolvedVariant || !this.#matchesSelection(resolvedVariant, selected)) {
                this.#setFormState(row, null);
                return false;
            }

            this.#lastResolved = { key: requestKey, id: String(resolvedVariant.id) };
            this.#setFormState(row, resolvedVariant);
            if (typeof resolvedVariant.price === 'number' && resolvedVariant.price > 0) {
                this.#updatePrice(row, resolvedVariant);
            }
            this.#updateStock(row, resolvedVariant);
            return true;
        }

        /**
         * Click guard on the row's "Add to order" button. Runs while the click
         * is still dispatching — strictly before the form's submit event, so
         * before product-form-component snapshots FormData.
         *
         * Re-derives the variant id from the CURRENTLY VISIBLE select values
         * and re-asserts it into the hidden input. This is the last line of
         * defense for every way the hidden id can silently diverge from the
         * visible selection (a morph re-render resetting the input, a stale
         * async resolution, a detached listener): whatever is displayed is
         * what gets added to the cart, or nothing is.
         * @param {MouseEvent} event
         */
        #onAddClick = (event) => {
            const target = event.target;
            if (!(target instanceof Element) || !target.closest('.catalog-row__add-btn')) return;

            const row = this.#row;
            if (!row) return;

            const input = row.querySelector('input[name="id"][ref="variantId"]');
            if (!(input instanceof HTMLInputElement)) return;

            const selects = this.#optionSelects();
            // Default-variant product: the static id is the only variant.
            if (selects.length === 0) return;

            this.#refreshVariantsFromDom();
            const selected = selects.map((s) => s.value);
            const variant = this.#variants.find((v) => this.#matchesSelection(v, selected)) || null;

            if (variant) {
                input.value = String(variant.id);
                input.disabled = false;
                return;
            }

            const selectedOptionValueIds = selects.map((s) => s.selectedOptions[0]?.dataset.optionValueId ?? '');
            const requestKey = selectedOptionValueIds.join(',');

            if (this.#lastResolved && this.#lastResolved.key === requestKey) {
                input.value = this.#lastResolved.id;
                input.disabled = false;
                return;
            }

            // The selection can't be verified synchronously. Block this
            // submission, resolve the variant remotely, and resubmit once the
            // id is confirmed. Never let an unverified id reach the cart.
            event.preventDefault();
            event.stopImmediatePropagation();

            const form = input.form;
            this.#setPendingState(row);
            this.#resolveSelectedVariant(row, selected, selectedOptionValueIds, requestKey).then((verified) => {
                if (verified) form?.requestSubmit();
            });
        };

        /**
         * Resolve variants beyond Liquid's product.variants cap by asking
         * Shopify to render the product page for the selected option IDs.
         * @param {string[]} optionValueIds
         * @param {string} requestKey
         * @returns {Promise<Variant | null>}
         */
        async #fetchSelectedVariant(optionValueIds, requestKey) {
            const productUrl = this.dataset.productUrl;
            if (!productUrl || optionValueIds.some((id) => !id)) return null;

            this.#abortController?.abort();
            this.#abortController = new AbortController();
            this.#requestKey = requestKey;

            const url = new URL(productUrl, window.location.origin);
            url.searchParams.set('section_id', 'section-rendering-product-card');
            url.searchParams.set('option_values', optionValueIds.join(','));

            try {
                const response = await fetch(url.toString(), { signal: this.#abortController.signal });
                const text = await response.text();
                const html = new DOMParser().parseFromString(text, 'text/html');

                const jsonStr = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
                if (jsonStr) {
                    const variantData = JSON.parse(jsonStr);
                    if (variantData && variantData.id) {
                        return variantData;
                    }
                }

                return null;
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') return null;
                console.error(error);
                return null;
            }
        }

        /** @param {Element} row */
        #setPendingState(row) {
            const input = row.querySelector('input[name="id"][ref="variantId"]');
            if (input instanceof HTMLInputElement) {
                input.disabled = true;
            }

            const button = row.querySelector('.catalog-row__add-btn');
            if (button instanceof HTMLButtonElement) {
                button.disabled = true;
            }
        }

        /**
         * @param {Element} row
         * @param {Variant | null} variant
         */
        #setFormState(row, variant) {
            const input = row.querySelector('input[name="id"][ref="variantId"]');
            if (input instanceof HTMLInputElement) {
                if (variant) {
                    input.value = String(variant.id);
                    input.disabled = false;
                } else {
                    input.disabled = true;
                }
            }

            const button = row.querySelector('.catalog-row__add-btn');
            if (button instanceof HTMLButtonElement) {
                button.disabled = !variant;
            }
        }

        /**
         * @param {Element} row
         * @param {Variant | null} variant
         */
        #updatePrice(row, variant) {
            if (!variant) return;
            const priceEl = row.querySelector('.catalog-row__price');
            if (!priceEl) return;

            const formatMoney = window.Shopify?.formatMoney;
            if (!formatMoney) return;

            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                priceEl.innerHTML =
                    `<span class="catalog-row__price--sale">${formatMoney(variant.price)}</span> ` +
                    `<span class="catalog-row__price--compare"><s>${formatMoney(variant.compare_at_price)}</s></span>`;
            } else {
                priceEl.innerHTML = `<span>${formatMoney(variant.price)}</span>`;
            }
        }

        /**
         * Update the "Ships today" / "Ships in 3 to 7 days" indicator
         * based on the resolved variant's inventory. If the product's
         * `custom.stock_item` metafield is true, always show "Ships today" —
         * except for variants whose BC value is listed in the product's
         * `custom.unstocked_bcs` metafield (surfaced via data-unstocked-bcs),
         * which are never stocked and always show "Ships in 3 to 7 days".
         * When `data-minus-only-in-stock="true"` is set (cart inline search),
         * inventory_quantity is not exposed in that section context, so the
         * metafield is the only reliable stock signal — and only minus-sphere
         * variants (option1 starts with "-") are eligible to show "Ships today".
         * @param {Element} row
         * @param {Variant | null} variant
         */
        #updateStock(row, variant) {
            if (!variant) return;
            const stockEl = row.querySelector('[data-stock-indicator]');
            if (!(stockEl instanceof HTMLElement)) return;

            const alwaysInStock = stockEl.dataset.alwaysInStock === 'true';

            // BC values flagged as unstocked (data-unstocked-bcs, from the
            // product's custom.unstocked_bcs metafield) always ship in
            // 3 to 7 days, overriding stock_item and inventory quantities.
            let bcUnstocked = false;
            const unstockedBcsAttr = stockEl.dataset.unstockedBcs || '';
            if (unstockedBcsAttr !== '') {
                const bcIndex = Number.parseInt(stockEl.dataset.bcOptionIndex || '-1', 10);
                const bc = bcIndex >= 0 ? variantOptions(variant)[bcIndex] : undefined;
                if (typeof bc === 'string') {
                    bcUnstocked = unstockedBcsAttr.split(',').includes(bc.trim());
                }
            }

            let inStock;
            if (bcUnstocked) {
                inStock = false;
            } else if (stockEl.dataset.minusOnlyInStock === 'true') {
                const sphere = typeof variant.option1 === 'string' ? variant.option1.trim() : '';
                const isMinusSphere = sphere.startsWith('-');
                inStock = alwaysInStock && isMinusSphere;
            } else {
                const v = /** @type {any} */ (variant);
                const qty = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : 0;
                inStock = alwaysInStock || qty > 0;
            }

            const inText = stockEl.dataset.inStockText || 'Ships today';
            const outText = stockEl.dataset.outOfStockText || 'Ships in 3 to 7 days';

            stockEl.textContent = inStock ? inText : outText;
            stockEl.classList.toggle('catalog-row__stock--in', inStock);
            stockEl.classList.toggle('catalog-row__stock--out', !inStock);
        }

        /** @returns {HTMLSelectElement[]} */
        #optionSelects() {
            return /** @type {HTMLSelectElement[]} */ (
                Array.from(this.querySelectorAll('select[name^="options"]'))
            );
        }

        /**
         * (Re)load the variants array from the embedded JSON script tag in
         * the current DOM. Safe to call repeatedly. Used both at init and on
         * every selection change so the picker always matches against the
         * variants of the product currently rendered into this row, even
         * after morph-based updates have swapped the row's product.
         */
        #refreshVariantsFromDom() {
            const jsonEl = this.querySelector('script[type="application/json"][data-catalog-variants]');
            if (!jsonEl) return;
            try {
                this.#variants = JSON.parse(jsonEl.textContent || '[]');
            } catch {
                this.#variants = [];
            }
        }
    }

    customElements.define('catalog-row-picker', CatalogRowPicker);
})();
